/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Directive, ElementRef, OnDestroy, OnInit} from '@angular/core';
import {PopoverDirective} from 'ngx-bootstrap/popover';
import {PositioningService} from 'ngx-bootstrap/positioning';
import {Subscription} from 'rxjs';

const VIEWPORT_PAD_PX = 12;

/**
 * After a popover opens, keep a small gap from the viewport edges.
 *
 * ngx-bootstrap's preventOverflow uses padding 0 and will rewrite transform on
 * each positioning pass, so this re-applies the clamp after every update while
 * the popover is open.
 */
@Directive({
  standalone: false,
  selector: '[popover][adnClampPopoverViewport]'
})
export class ClampPopoverViewportDirective implements OnInit, OnDestroy {
  private readonly subscriptions = new Subscription();
  private positionSubscription: Subscription | undefined;

  constructor(
    private readonly popover: PopoverDirective,
    private readonly host: ElementRef<HTMLElement>,
    private readonly positioning: PositioningService
  ) {
  }

  ngOnInit(): void {
    this.subscriptions.add(this.popover.onShown.subscribe(() => this.startClamping()));
    this.subscriptions.add(this.popover.onHidden.subscribe(() => this.stopClamping()));
  }

  ngOnDestroy(): void {
    this.stopClamping();
    this.subscriptions.unsubscribe();
  }

  private startClamping(): void {
    this.stopClamping();
    // ngx-bootstrap's component-loader calcPosition()s on a rAF loop while
    // open, and preventOverflow uses padding 0. PositioningService applies
    // styles in its event$ handler first (subscribed at construction); we
    // subscribe later and clamp synchronously so our gap wins each tick.
    this.positionSubscription = this.positioning.event$.subscribe(() => {
      this.clamp();
    });
    // Cover the first paint if onShown raced ahead of the position element.
    requestAnimationFrame(() => this.clamp());
  }

  private stopClamping(): void {
    this.positionSubscription?.unsubscribe();
    this.positionSubscription = undefined;
  }

  private clamp(): void {
    if (!this.popover.isOpen()) {
      return;
    }
    const el = this.findPopoverElement();
    if (!el) {
      return;
    }
    const pad = VIEWPORT_PAD_PX;
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
    const rect = el.getBoundingClientRect();
    let dx = 0;
    let dy = 0;
    if (rect.right > viewportWidth - pad) {
      dx = viewportWidth - pad - rect.right;
    }
    if (rect.left + dx < pad) {
      dx += pad - (rect.left + dx);
    }
    if (rect.bottom > viewportHeight - pad) {
      dy = viewportHeight - pad - rect.bottom;
    }
    if (rect.top + dy < pad) {
      dy += pad - (rect.top + dy);
    }
    if (dx || dy) {
      this.applyOffset(el, dx, dy);
    }
    if (dx) {
      this.syncArrow(el);
    }
  }

  private findPopoverElement(): HTMLElement | null {
    const describedBy = this.host.nativeElement.getAttribute('aria-describedby');
    if (describedBy) {
      const byAria = document.getElementById(describedBy);
      if (byAria) {
        return byAria;
      }
    }
    const byId = document.getElementById(`ngx-popover-${this.popover.popoverId}`);
    if (byId) {
      return byId;
    }
    const containers = document.querySelectorAll<HTMLElement>('body > popover-container.popover');
    return containers.length ? containers[containers.length - 1] : null;
  }

  private applyOffset(el: HTMLElement, dx: number, dy: number): void {
    const transform = el.style.transform || window.getComputedStyle(el).transform || '';
    const match3d = /translate3d\(\s*([-\d.]+)px\s*,\s*([-\d.]+)px\s*,\s*([-\d.]+)px\s*\)/.exec(transform);
    if (match3d) {
      el.style.transform = `translate3d(${parseFloat(match3d[1]) + dx}px, ${parseFloat(match3d[2]) + dy}px, ${match3d[3]}px)`;
      return;
    }
    const matchMatrix = /matrix\(\s*([-\d.]+),\s*([-\d.]+),\s*([-\d.]+),\s*([-\d.]+),\s*([-\d.]+),\s*([-\d.]+)\s*\)/.exec(transform);
    if (matchMatrix) {
      el.style.transform = `translate3d(${parseFloat(matchMatrix[5]) + dx}px, ${parseFloat(matchMatrix[6]) + dy}px, 0px)`;
      return;
    }
    const left = parseFloat(el.style.left);
    const top = parseFloat(el.style.top);
    if (!Number.isNaN(left) || !Number.isNaN(top)) {
      if (!Number.isNaN(left)) {
        el.style.left = `${left + dx}px`;
      }
      if (!Number.isNaN(top)) {
        el.style.top = `${top + dy}px`;
      }
      return;
    }
    el.style.transform = `translate3d(${dx}px, ${dy}px, 0px)`;
  }

  /** Point the arrow at the trigger after a horizontal shift. */
  private syncArrow(el: HTMLElement): void {
    const arrow = el.querySelector<HTMLElement>('.arrow, .popover-arrow');
    if (!arrow) {
      return;
    }
    const hostRect = this.host.nativeElement.getBoundingClientRect();
    const popRect = el.getBoundingClientRect();
    const arrowWidth = arrow.offsetWidth || 0;
    const center = hostRect.left + hostRect.width / 2 - popRect.left - arrowWidth / 2;
    const maxLeft = Math.max(0, el.offsetWidth - arrowWidth - 5);
    arrow.style.left = `${Math.max(0, Math.min(maxLeft, center))}px`;
  }
}
